import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import BookingSummaryCard from './components/BookingSummaryCard';
import PaymentMethodSelector from './components/PaymentMethodSelector';
import PaymentStatusModal from './components/PaymentStatusModal';
import SecurityIndicators from './components/SecurityIndicators';
import PaymentProgress from './components/PaymentProgress';
import { usePaymentStatus } from '../../components/ui/PaymentStatusBanner';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const PaymentProcessing = () => {
  const navigate = useNavigate();
  const { showProcessing, showSuccess, showError } = usePaymentStatus();
  
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('cards');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [transactionId, setTransactionId] = useState(null);
  const [currentStep] = useState(3); // Payment step

  // Mock booking data
  const mockBookingData = {
    package: {
      name: "Ooty Hill Station Adventure",
      location: "Ooty, Tamil Nadu",
      duration: 4,
      image: "https://images.pexels.com/photos/1591373/pexels-photo-1591373.jpeg?auto=compress&cs=tinysrgb&w=400"
    },
    checkIn: "2025-01-20",
    checkOut: "2025-01-24",
    travelers: {
      adults: 2,
      children: 1
    },
    roomType: "Deluxe Room",
    traveler: {
      name: "Rajesh Kumar",
      email: "rajesh.kumar@email.com",
      phone: "+91 98765 43210"
    },
    pricing: {
      packageCost: 45000,
      serviceFee: 2500,
      gst: 8550,
      discount: 5000,
      total: 51050
    },
    bookingReference: "DT2025071400001"
  };

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login-registration');
      return;
    }

    // Simulate loading booking data
    const timer = setTimeout(() => {
      // Booking data loaded
    }, 1000);

    return () => clearTimeout(timer);
  }, [navigate]);

  const handlePaymentMethodSelect = (method) => {
    setSelectedPaymentMethod(method);
  };

  const generateTransactionId = () => {
    return `TXN${Date.now()}${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
  };

  const simulatePaymentProcessing = async () => {
    setIsProcessing(true);
    setShowModal(true);
    setPaymentStatus('processing');
    showProcessing("Processing your payment...", "Please do not close this window");

    // Simulate payment processing time
    await new Promise(resolve => setTimeout(resolve, 3000));

    // Simulate payment result (80% success rate)
    const isSuccess = Math.random() > 0.2;
    const txnId = generateTransactionId();
    setTransactionId(txnId);

    if (isSuccess) {
      setPaymentStatus('success');
      showSuccess(
        "Payment successful! Your booking is confirmed.",
        `Transaction ID: ${txnId}`,
        {
          label: "View Booking",
          onClick: () => navigate('/user-dashboard')
        }
      );
      
      // Simulate email receipt
      setTimeout(() => {
        console.log('Email receipt sent to:', mockBookingData.traveler.email);
      }, 1000);
    } else {
      setPaymentStatus('failed');
      showError(
        "Payment failed. Please try again.",
        "Your card was declined or there was a network issue.",
        {
          label: "Contact Support",
          onClick: () => navigate('/real-time-customer-support')
        }
      );
    }

    setIsProcessing(false);
  };

  const handlePaymentSubmit = () => {
    simulatePaymentProcessing();
  };

  const handleModalClose = () => {
    setShowModal(false);
    if (paymentStatus === 'success') {
      navigate('/user-dashboard');
    }
  };

  const handleRetryPayment = () => {
    setShowModal(false);
    setPaymentStatus(null);
    setTransactionId(null);
  };

  const handleBackToBooking = () => {
    navigate('/user-dashboard');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-2 mb-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBackToBooking}
                className="mr-2"
              >
                <Icon name="ArrowLeft" size={20} />
              </Button>
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground">
                  Complete Your Payment
                </h1>
                <p className="text-muted-foreground mt-1">
                  Secure payment processing for your travel booking
                </p>
              </div>
            </div>
            
            {/* Security Badge */}
            <div className="flex items-center space-x-2 text-success">
              <Icon name="Shield" size={16} />
              <span className="text-sm font-medium">
                Secured by 256-bit SSL encryption
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Payment Form */}
            <div className="lg:col-span-2 space-y-6">
              <PaymentProgress currentStep={currentStep} />
              
              <PaymentMethodSelector
                selectedMethod={selectedPaymentMethod}
                onMethodSelect={handlePaymentMethodSelect}
                onPaymentSubmit={handlePaymentSubmit}
                isProcessing={isProcessing}
              />
            </div>

            {/* Right Column - Booking Summary & Security */}
            <div className="space-y-6">
              <BookingSummaryCard bookingData={mockBookingData} />
              
              <SecurityIndicators />
            </div>
          </div>

          {/* Additional Information */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Cancellation Policy */}
            <div className="bg-card rounded-lg border border-border p-6 shadow-soft">
              <div className="flex items-center space-x-2 mb-4">
                <Icon name="FileText" size={20} className="text-secondary" />
                <h3 className="font-heading font-semibold text-foreground">
                  Cancellation Policy
                </h3>
              </div>
              <div className="space-y-3 text-sm text-muted-foreground">
                <div className="flex items-start space-x-2">
                  <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                  <span>Free cancellation up to 48 hours before check-in</span>
                </div>
                <div className="flex items-start space-x-2">
                  <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                  <span>50% refund for cancellations 24-48 hours before</span>
                </div>
                <div className="flex items-start space-x-2">
                  <Icon name="X" size={14} className="text-error mt-0.5 flex-shrink-0" />
                  <span>No refund for cancellations within 24 hours</span>
                </div>
              </div>
            </div>

            {/* Customer Reviews */}
            <div className="bg-card rounded-lg border border-border p-6 shadow-soft">
              <div className="flex items-center space-x-2 mb-4">
                <Icon name="Star" size={20} className="text-warning" />
                <h3 className="font-heading font-semibold text-foreground">
                  Customer Reviews
                </h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Icon key={star} name="Star" size={14} className="text-warning fill-current" />
                    ))}
                  </div>
                  <span className="text-sm font-medium text-foreground">4.8/5</span>
                  <span className="text-sm text-muted-foreground">(2,847 reviews)</span>
                </div>
                <div className="text-sm text-muted-foreground">
                  "Excellent service and secure payment process. Highly recommended for Tamil Nadu travel bookings!"
                </div>
                <div className="text-xs text-muted-foreground">
                  - Priya S., Verified Customer
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Status Modal */}
      <PaymentStatusModal
        isOpen={showModal}
        status={paymentStatus}
        onClose={handleModalClose}
        onRetry={handleRetryPayment}
        bookingReference={mockBookingData.bookingReference}
        transactionId={transactionId}
        amount={mockBookingData.pricing.total}
      />
    </div>
  );
};

export default PaymentProcessing;